
import { useRouter } from 'next/router'
import { cn } from '~shared/utils/cn'
import imgDefault from './images/hero.jpg'
import Image from 'next/image'
import { blendWithWhite } from '~shared/utils/blendWithWhite'
import { Fragment, useState } from 'react'
import { LeadFormDialog } from '~widgets/lead-form/components/lead-form-dialog'
import { Button } from '~shared/components/button-v2'
import { getProgramData } from '~shared/api/single-program-data/single-program-data'
import { useCalculateStudyDuration } from '~shared/hooks/useCalculateStudyDuration'
import { useCalculateClosestEnrollment } from '~shared/hooks/useCalculateClosestEnrollment'
import { calculateRuCase } from '~shared/utils/calculateRuCase'
import { getEffectiveDiscount } from '~shared/utils/discount'

// TODO: временно — статика для 5 программ, пока документы не заведены в CMS.
const TEMP_CERTIFICATE_ADDON_SLUGS = [
  'psiholog-konsultant',
  'geshtalt-terapiya',
  'kognitivno-povedencheskaya-psihoterapiya',
  'psihosomatika-i-telesno-orientirovannaya-terapiya',
  'art-terapiya'
]

export type THeroProps = {
  program: Awaited<ReturnType<typeof getProgramData>>
}

export const HeroSection = ({ program }: THeroProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const { locale } = useRouter()

  const activeFormat = [
    {
      studyForm: 'online' as const,
      price: program?.costOnline?.priceOnline,
      discount: program?.costOnline?.discountOnline,
      discountMonths: program?.costOnline?.discountMonthsOnline,
      discountUntil: program?.costOnline?.discountUntilOnline,
      studyDurationYears: program?.costOnline?.studyDurationYearsOnline,
      studyDurationMonths: program?.costOnline?.studyDurationMonthsOnline,
      closestEnrollment: program?.costOnline?.closestEnrollmentOnline,
      studyHours: program?.costOnline?.studyHoursOnline || null
    },
    {
      studyForm: 'mixed' as const,
      price: program?.cost?.price,
      discount: program?.cost?.discount,
      discountMonths: program?.cost?.discountMonths,
      discountUntil: program?.cost?.discountUntil,
      studyDurationYears: program?.cost?.studyDurationYears,
      studyDurationMonths: program?.cost?.studyDurationMonths,
      closestEnrollment: program?.cost?.closestEnrollment,
      studyHours: program?.cost?.studyHours || null
    },
    {
      studyForm: 'offline' as const,
      price: program?.costOffline?.priceOffline,
      discount: program?.costOffline?.discountOffline,
      discountMonths: program?.costOffline?.discountMonthsOffline,
      discountUntil: program?.costOffline?.discountUntilOffline,
      studyDurationYears: program?.costOffline?.studyDurationYearsOffline,
      studyDurationMonths: program?.costOffline?.studyDurationMonthsOffline,
      closestEnrollment: program?.costOffline?.closestEnrollmentOffline,
      studyHours: program?.costOffline?.studyHoursOffline || null
    }
  ].find(format => format.price !== null && format.price !== undefined)

  const isCourse = ['kursy'].includes(program?.category?.slug ?? '')
  const effectiveDiscount = getEffectiveDiscount({
    formatDiscount: activeFormat?.discount,
    category: program?.category,
    faculty: program?.faculty
  })

  const discountUntilFormatted = (() => {
    if (!activeFormat?.discountUntil) return null
    const months = ['января','февраля','марта','апреля','мая','июня','июля','августа','сентября','октября','ноября','декабря']
    const date = new Date(activeFormat.discountUntil)
    return `${date.getDate()} ${months[date.getMonth()]}`
  })()

  const calculatedStudyDuration = useCalculateStudyDuration({
    months: activeFormat?.studyDurationMonths,
    years: activeFormat?.studyDurationYears
  })

  const calculatedClosestEnrollment = useCalculateClosestEnrollment({
    timestamp: activeFormat?.closestEnrollment || null,
    isCourse
  })

  const imageProps =
    program?.image?.url && program?.image?.height && program?.image?.width
      ? {
          src: program.image.url,
          width: program.image.width,
          height: program.image.height,
          alt: program.image.alternativeText || 'Человек работает'
        }
      : {
          src: imgDefault,
          alt: 'Человек работает',
          width: 600,
          height: 400
        }

  const isMba = program?.category?.slug === 'mba'

  const availableStudyForms = (isCourse || isMba)
    ? 'Дистанционно'
    : [
        program?.costOnline?.priceOnline != null ? 'Заочная' : null,
        program?.cost?.price != null ? 'Очно-заочная' : null,
        program?.costOffline?.priceOffline != null ? 'Очная' : null
      ].filter(Boolean).join(' / ')

  const diplomas = program?.diplomas?.length
    ? [...program.diplomas].sort((a, b) => (a?.rank ?? Infinity) - (b?.rank ?? Infinity))
    : []

  const hasCertificateAddon = TEMP_CERTIFICATE_ADDON_SLUGS.includes(
    program?.slug ?? ''
  )

  const testimonials = [
    {
      label: null,
      name: 'Ближайшее зачисление',
      value: calculatedClosestEnrollment
    },
    {
      label: isCourse ? 'Как сократить?' : null,
      name: 'Срок обучения',
      value: calculatedStudyDuration
    },
    {
      label: null,
      name: 'Форма обучения',
      value: availableStudyForms || 'Дистанционная'
    },
    {
      label: null,
      name: hasCertificateAddon
        ? 'Документы по окончании обучения'
        : 'Документ по окончании обучения',
      value: hasCertificateAddon
        ? 'Диплом о переподготовке и Удостоверение о повышении квалификации'
        : diplomas?.[0]?.label || 'Диплом государственного образца'
    },
    ...(isCourse && activeFormat?.studyHours
      ? [
          {
            label: null,
            name: 'Количество часов',
            value: `${activeFormat.studyHours} ${calculateRuCase('час', activeFormat.studyHours, locale)}`
          }
        ]
      : [
          {
            label: null,
            name: 'Уровень образования',
            value: program?.category?.label || program?.category?.name || 'Курс'
          }
        ])
  ]

  return (
    <section
      style={{ backgroundColor: program?.faculty?.color ?? undefined }}
      className={cn('py-[80px] text-white')}>
      <div
        className={cn(
          'mx-auto px-[16px] box-border',
          'max-w-[672px]',
          'md:max-w-[832px]',
          '2lg:max-w-[1280px]'
        )}>
        <div className='flex flex-col gap-10 md:gap-16 2lg:flex-row 2lg:gap-8'>
          <div className='flex flex-col 2lg:flex-[4]'>
            <div className='flex items-center gap-6 mb-4 md:mb-8 2lg:mb-14'>
              <span className='inline-flex items-center px-4 h-[35px] rounded-full text-sm font-medium border-white border-[1px]'>
                {program?.category?.label || program?.category?.name || 'Курс'}
              </span>
              {effectiveDiscount && (
                <div className='flex items-center gap-2'>
                  <span
                    className='px-2 py-1 rounded-full text-base font-normal leading-[120%] bg-[var(--alpha)]'
                    style={{ color: program?.faculty?.color || 'var(--beta)' }}>
                    Скидка - {effectiveDiscount}%
                  </span>
                  {discountUntilFormatted && (
                    <span className='text-sm'>
                      до {discountUntilFormatted}
                    </span>
                  )}
                </div>
              )}
            </div>
            <h1 className='text-[30px]/[100%] sm:text-[32px]/[100%] font-medium md:text-[42px]/[100%] 2lg:text-[72px]/[100%] mb-[24px] mt-0'>
              {program?.name || ''}
            </h1>
            <p className='text-base md:text-[20px]'>
              {program?.description ?? ''}
            </p>
          </div>
          <div className='2lg:flex-[3]'>
            <Image
              {...imageProps}
              className='w-full h-auto rounded-2xl object-cover'
            />
          </div>
        </div>

        <div
          className={cn(
            'p-[16px] 2lg:p-[32px] flex flex-col md:flex-row rounded-[24px] mt-[56px] items-stretch',
            'gap-[16px] md:gap-[20px] 2lg:gap-[40px]'
          )}
          style={
            program?.faculty?.color
              ? {
                  backgroundColor: blendWithWhite(program.faculty.color, 0.85)
                }
              : undefined
          }>
          {testimonials.map((item, idx) => (
            <Fragment key={idx}>
              <div
                className={cn(
                  'flex flex-col items-start gap-[16px] md:pt-[32px] relative max-w-[210px] 2lg:max-w-[250px]',
                  idx === 1 && 'min-w-[100px]'
                )}>
                {item.label && (
                  <>
                    <Button
                      size='slg'
                      variant='primary'
                      onClick={() => setIsOpen(true)}
                      className='md:absolute top-0 left-0 text-[10px] h-[20px] bg-white text-black px-[10px] rounded-[8px]'>
                      Как сократить?
                    </Button>
                    <LeadFormDialog
                      open={isOpen}
                      onOpenChange={setIsOpen}
                      buttonText='Подобрать программу'
                    />
                  </>
                )}
                <p className='text-[12px] 2lg:text-[14px] font-medium 2lg:text-nowrap'>
                  {item.name}
                </p>
                <p className='text-[12px]/[120%] 2lg:text-[14px]/[120%]'>
                  {item.value}
                </p>
              </div>
              {idx < testimonials.length - 1 && (
                <div className='w-full md:w-[1px] bg-white self-stretch min-h-[2px]' />
              )}
            </Fragment>
          ))}
        </div>
      </div>
    </section>
  )
}
